import { injectIf } from '@fastre/core/helperFunctions/object'
import { MarketingPackageSchema, UpdateMarketingPackageSchema } from '@fastre/core/schemas/marketingPackage'
import { zodResolver } from '@hookform/resolvers/zod'
import { AddRounded, Delete } from '@mui/icons-material'
import EditRounded from '@mui/icons-material/EditRounded'
import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormLabel,
    IconButton,
    List,
    ListItemButton,
    ListItemDecorator,
    Modal,
    ModalClose,
    ModalDialog,
    Sheet,
    Stack,
    Typography,
} from '@mui/joy'
import { useApi } from 'api'
import { useMarketingItemsApi, useMarketingPackagesApi } from 'apiProviders'
import AgentInput, { MultipleAgentInput } from 'components/agentInput'
import { SlotInput, SlotWrapper } from 'components/input'
import { dontCloseOnBackgroundClick } from 'components/modal'
import { useShowSnack } from 'components/snackbar'
import { MobileFriendlyStack } from 'components/stack'
import { useMaybeState } from 'helperFunctions/react'
import { useOrgId } from 'helpers'
import { groupBy, prop, sortBy } from 'ramda'
import { useMemo, useState } from 'react'
import { Controller, SubmitHandler, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { v4 as uuid } from 'uuid'

const EditPackageModal = ({ mpackage, close }: { mpackage: MarketingPackageSchema; close: () => void }) => {
    const isNew = mpackage.packageId == undefined
    const api = useApi()
    const showSnack = useShowSnack()
    const packagesApi = useMarketingPackagesApi()
    const itemsApi = useMarketingItemsApi()
    const orgId = useOrgId()

    const maybeGroupedSortedItems = useMemo(() => {
        return itemsApi.maybeData.map(items => {
            const grouped = groupBy(prop('itemCategory'), items)
            const a = Object.entries(grouped)
            return sortBy(([cat]) => cat, Object.entries(grouped))
        })
    }, [packagesApi.loading])

    const { register, control, handleSubmit, formState, getValues, setValue } =
        useForm<MarketingPackageSchema>({
            defaultValues: {
                ...injectIf(isNew, {
                    parentId: orgId!,
                    packageId: uuid(),
                }),
                ...mpackage,
            },
            resolver: zodResolver(UpdateMarketingPackageSchema),
        })

    const {
        fields: items,
        append: addItem,
        prepend,
        remove: removeItem,
        swap,
        move,
        insert: insertItem,
    } = useFieldArray({
        control,
        name: 'packageItems',
    })

    const itemsWatch = useWatch({ control, name: 'packageItems' })

    const [loading, setLoading] = useState(false)

    const onSubmit: SubmitHandler<MarketingPackageSchema> = async data => {
        setLoading(true)
        try {
            await api.post(`/marketingpackage/addupdate`, data).then(prop('data'))
            packagesApi.refresh()
            close()
            showSnack('Marketing package saved', 'success')
        } catch (e) {
            console.error(e)
            showSnack('Error saving marketing package', 'danger')
        } finally {
            setLoading(false)
        }
    }

    if (Object.keys(formState.errors).length > 0) {
        console.log(formState.errors)
    }

    //const widthProps = getInputWidth()

    return (
        <ModalDialog minWidth={800}>
            <DialogTitle>{isNew ? 'Create' : 'Edit'} Marketing Package</DialogTitle>
            <ModalClose />
            <DialogContent sx={{ overflow: 'auto' }}>
                <form
                    noValidate
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Controller
                        name="packageName"
                        control={control}
                        render={field => (
                            <SlotInput
                                label="Package Name"
                                type="text"
                                {...field}
                            />
                        )}
                    />
                    <Controller
                        name="userIds"
                        control={control}
                        render={field => (
                            <Box sx={{ mt: 2 }}>
                                <SlotWrapper
                                    label="Available To"
                                    {...field}
                                >
                                    <MultipleAgentInput
                                        filter={prop('salesAgent')}
                                        placeholder="All Agents"
                                        {...field.field}
                                        value={field.field.value ?? []}
                                    />
                                </SlotWrapper>
                            </Box>
                        )}
                    />
                    <br />
                    <Typography
                        sx={{ mt: 2 }}
                        fontStyle="italic"
                    >
                        Add Marketing Items:
                    </Typography>
                    <Stack
                        direction="row"
                        gap={1}
                    >
                        <Stack sx={{ flex: '1' }}>
                            {maybeGroupedSortedItems
                                .map(groupedSortedItems =>
                                    groupedSortedItems.map(([category, categoryItems]) => (
                                        <>
                                            <Typography
                                                sx={{
                                                    textTransform: 'uppercase',
                                                    mb: 1,
                                                    fontWeight: 'bold',
                                                    fontSize: '0.875rem', // Match the font size of ListSubheader
                                                    lineHeight: 1,
                                                    color: 'text.secondary', // Subtle text color to reduce brightness
                                                    padding: '4px 8px', // Adds padding for better spacing
                                                    borderRadius: '4px', // Optional rounded corners
                                                }}
                                            >
                                                <br />
                                                {category}
                                            </Typography>
                                            {categoryItems
                                                .filter(item => !items.some(i => i.itemId === item.itemId))
                                                .sort((a, b) => a.itemName.localeCompare(b.itemName)) // Sort items alphabetically by itemName
                                                .map(item => (
                                                    <Sheet
                                                        key={item.itemId}
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 2,
                                                            alignItems: 'center',
                                                            padding: 1, // Add padding to create spacing inside the box
                                                            borderRadius: 1, // Rounded corners
                                                            boxShadow: 1, // Optional shadow for effect
                                                            width: 'auto', // Ensures the width adjusts based on content
                                                            maxWidth: '100%', // Prevents it from exceeding the container's width
                                                        }}
                                                    >
                                                        <IconButton
                                                            variant="outlined"
                                                            onClick={() => addItem(item)}
                                                            size="sm"
                                                            sx={{
                                                                display: 'flex', // Ensure it's properly centered
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                            }}
                                                        >
                                                            <AddRounded fontSize="small" />
                                                        </IconButton>
                                                        <Typography
                                                            sx={{
                                                                lineHeight: 1, // Ensures consistent vertical spacing
                                                                color: 'text.secondary', // Subtle text color to reduce brightness
                                                            }}
                                                        >
                                                            {item.itemName} - ${item.itemPrice.toFixed(2)}
                                                        </Typography>
                                                    </Sheet>
                                                ))}
                                        </>
                                    )),
                                )
                                .orSome([])}
                        </Stack>
                    </Stack>

                    <Stack gap={1}>
                        {items.length > 0 && (
                            <Typography sx={{ fontWeight: 600, fontSize: 20, mb: 1, mt: 4 }}>
                                Items
                            </Typography>
                        )}

                        <Sheet
                            sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: 'center',
                                padding: 1, // Add padding to create spacing inside the box
                                borderRadius: 1, // Rounded corners
                                boxShadow: 1, // Optional shadow for effect
                                width: 'auto', // Ensures the width adjusts based on content
                                maxWidth: '100%', // Prevents it from exceeding the container's width
                            }}
                        ></Sheet>
                        {items.map((item, itemIndex) => (
                            <MobileFriendlyStack
                                key={itemIndex}
                                direction="row"
                                gap={2}
                                alignItems="center"
                            >
                                {/* Display the itemIndex 

                                <Controller
                                    name={`packageItems.${itemIndex}.itemCategory`}
                                    control={control}
                                    render={({ field, formState }) => (
                                        <SlotWrapper
                                            field={field} // Pass the field prop
                                            formState={formState} // Pass the formState prop if required
                                            label={itemIndex == 0 ? 'Category' : ''} // Conditional label
                                        >
                                            <Select
                                                {...field} // Spread field here for value and onChange
                                                onChange={(e, value: any) =>
                                                    setValue(`packageItems.${itemIndex}.itemCategory`, value)
                                                }
                                                sx={{
                                                    width: '246px', // Set desired width
                                                    maxWidth: '100%', // Optional: Prevents overflow
                                                }}
                                            >
                                                {sortBy((x) => x, MarketingItemCategories).map((cat) => (
                                                    <Option key={cat} value={cat}>
                                                        {cat}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </SlotWrapper>
                                    )}
                                />*/}

                                <Controller
                                    name={`packageItems.${itemIndex}.itemName`}
                                    control={control}
                                    render={field => (
                                        <SlotInput
                                            label={itemIndex == 0 ? 'Name' : ''} // Conditional label
                                            {...field}
                                        />
                                    )}
                                />
                                <Controller
                                    name={`packageItems.${itemIndex}.itemPrice`}
                                    control={control}
                                    render={field => (
                                        <SlotInput
                                            label={itemIndex == 0 ? 'Price' : ''} // Conditional label
                                            type="number"
                                            startDecorator="$"
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'flex-end', // Align content to the right
                                                '& .MuiInputBase-root': { textAlign: 'right' }, // Align text to the right
                                            }}
                                            {...field}
                                            onChange={e => {
                                                console.log('new val', e)
                                                field.field.onChange(e)
                                            }}
                                        />
                                    )}
                                />
                                <IconButton
                                    variant="outlined"
                                    onClick={() => removeItem(itemIndex)}
                                    //size="sm"
                                >
                                    <Delete /*fontSize="small"*/ />
                                </IconButton>
                            </MobileFriendlyStack>
                        ))}

                        {/* Add New Item Button */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            gap={1}
                        >
                            <IconButton
                                variant="outlined"
                                onClick={() =>
                                    addItem({ itemCategory: 'Advertising', itemName: '', itemPrice: 0 })
                                }
                                size="sm"
                                sx={{ width: 'auto' }} // Button size adjusts to content
                            >
                                <AddRounded fontSize="small" />
                            </IconButton>
                            <Typography> Add new </Typography> {/* Adjusted text display */}
                        </Stack>

                        {/* Row for Total Price - Only displayed when there are items */}
                        {items.length > 0 && (
                            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'row' }}>
                                {/* Placeholder to match the "itemName" column */}
                                <Typography sx={{ width: 207 }}>
                                    <b>
                                        <i>Total Package Price:</i>
                                    </b>
                                </Typography>
                                <Typography sx={{ ml: 2 }}>
                                    <b>
                                        <i>
                                            $
                                            {itemsWatch
                                                .reduce((acc, item) => acc + item.itemPrice, 0)
                                                .toFixed(2)}
                                        </i>
                                    </b>
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </form>
            </DialogContent>
            <DialogActions>
                <Button
                    //type="submit"
                    loading={loading}
                    onClick={handleSubmit(onSubmit)}
                >
                    Save
                </Button>
                <Button
                    variant="outlined"
                    onClick={close}
                >
                    Cancel
                </Button>
                {!isNew && (
                    <Box
                        sx={{
                            flexGrow: 1,
                        }}
                    >
                        <Button
                            color="danger"
                            variant="outlined"
                            loading={loading}
                            onClick={async () => {
                                setLoading(true)
                                await api.post('/marketingpackage/delete', {
                                    parentId: orgId,
                                    packageId: mpackage.packageId,
                                })
                                await packagesApi.refresh()
                                close()
                            }}
                        >
                            Delete
                        </Button>
                    </Box>
                )}
            </DialogActions>
        </ModalDialog>
    )
}

const MarketingSettings = () => {
    const packagesApi = useMarketingPackagesApi()

    const [maybeEditPackage, setEditPackage] = useMaybeState<MarketingPackageSchema>()
    const [userFilter, setUserFilter] = useState<string | null>()

    return (
        <>
            <FormControl>
                <FormLabel>Filter by Agent</FormLabel>
                <AgentInput
                    value={userFilter}
                    onChange={setUserFilter}
                    filter={prop('salesAgent')}
                    sx={{
                        width: '300px', // Adjust to desired width
                        maxWidth: '100%', // Ensures it doesn't overflow
                    }}
                />
            </FormControl>
            <Box sx={{ maxWidth: 320, margin: 'left', mt: 1 }}>
                <Stack
                    spacing={2}
                    sx={{ mt: 1 }}
                >
                    <List
                        component="nav"
                        sx={{ maxWidth: 320, borderRadius: 'sm' }}
                    >
                        {packagesApi.maybeData
                            .map(packages =>
                                packages
                                    .filter(
                                        p =>
                                            !userFilter ||
                                            p.userIds == undefined ||
                                            p.userIds.length == 0 ||
                                            p.userIds.includes(userFilter),
                                    )
                                    .sort((a, b) => a.packageName?.localeCompare(b.packageName ?? '') ?? 0) // Sort by packageName
                                    .map(p => (
                                        <ListItemButton
                                            key={p.packageId}
                                            onClick={() => setEditPackage(p)}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center', // Aligns content vertically centered
                                                p: 1, // Padding for internal spacing
                                                borderRadius: '8px', // Rounded corners
                                                mb: 1, // Bottom margin for spacing
                                                border: '0.5px solid transparent', // Transparent border to maintain size
                                                transition:
                                                    'background-color 0.3s, transform 0.3s, border-color 0.3s', // Smooth transition for hover effects
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)', // Slight background change on hover
                                                    border: '0.5px solid', // Border appears on hover
                                                    borderColor: 'primary.main',
                                                    '& *': {
                                                        filter: 'brightness(1.1)', // Brightens all child elements on hover
                                                    },
                                                },
                                            }}
                                        >
                                            <ListItemDecorator
                                                sx={{
                                                    minWidth: 'auto',
                                                    mr: 1,
                                                    display: 'flex',
                                                    verticalAlignItems: 'bottom',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                }}
                                            >
                                                <Box>
                                                    <EditRounded />
                                                </Box>
                                                <Typography>{p.packageName}</Typography>
                                            </ListItemDecorator>
                                        </ListItemButton>
                                    )),
                            )
                            .orSome([])}
                    </List>

                    <Box>
                        <Button
                            onClick={() =>
                                setEditPackage({
                                    packageName: '',
                                    packageItems: [],
                                })
                            }
                            sx={{ mt: 2 }}
                            variant="outlined"
                        >
                            New Package
                        </Button>
                    </Box>
                </Stack>
                <Modal
                    open={maybeEditPackage.isSome()}
                    onClose={dontCloseOnBackgroundClick(() => setEditPackage(undefined))}
                >
                    {maybeEditPackage
                        .map(p => (
                            <EditPackageModal
                                mpackage={p}
                                close={() => setEditPackage(undefined)}
                            />
                        ))
                        .orSome(<></>)}
                </Modal>
            </Box>
        </>
    )
}

export default MarketingSettings
